// Layout — app shell for UX v0.7.
//
// Single 48 px top bar with the Carbon mark, version, breadcrumb-like
// slot, and theme/user controls. The old PipelineStrip is gone — the
// per-project phase state now lives inside the Command Center header
// where it actually belongs.
//
// Plan §P2.18 / §P2.19 (UI Workflow Consistency Audit) +
// Carbon expansion:
//
//   * The theme toggle now cycles across three modes — dark → light →
//     carbon → dark — instead of binary flipping. Icons mirror the
//     CURRENT mode (matches the user's expectation: a sun icon
//     means "I'm in light mode", a moon means "I'm in dark", a "C"
//     badge means "I'm in Carbon").
//   * The Keyboard-icon button opens the global shortcuts cheat-sheet
//     (the ``?`` global key opens the same overlay).

import { Outlet, Link, useNavigate } from 'react-router-dom'
import { Keyboard, LogOut, Moon, Sparkles, Sun } from 'lucide-react'

import { useThemeStore, type ThemeMode } from '@/stores/themeStore'
import { useAuthStore } from '@/stores/useAuthStore'

/**
 * Marketing wordmark + display version shown in the top-left of the
 * app shell. The display version is intentionally decoupled from the
 * backend ``/healthz`` ``version`` field — the user wants to ship
 * the Carbon rebrand independently of any backend version bump.
 */
const APP_BRAND = 'CARBON'
const APP_DISPLAY_VERSION = 'v1.01'

export interface LayoutProps {
  /** Opens the global keyboard cheat sheet. Wired by ``App.tsx``. */
  onOpenShortcuts?: () => void
}

// Friendly copy for the cycle tooltip — surfaces both the current mode
// and the next one so the user knows what one click will do.
const THEME_LABEL: Record<ThemeMode, string> = {
  dark: 'Dark',
  light: 'Light',
  carbon: 'Carbon',
}
const NEXT_LABEL: Record<ThemeMode, string> = {
  dark: 'Light',
  light: 'Carbon',
  carbon: 'Dark',
}

export function Layout({ onOpenShortcuts }: LayoutProps) {
  const theme = useThemeStore((s) => s.theme)
  const cycleTheme = useThemeStore((s) => s.cycle)
  const navigate = useNavigate()
  // The logout button only renders when the backend has credentials
  // configured AND we actually have a session — otherwise there is
  // nothing to log out of.
  const authEnabled = useAuthStore((s) => s.authEnabled)
  const user = useAuthStore((s) => s.user)
  const signOut = useAuthStore((s) => s.signOut)

  async function handleLogout() {
    await signOut()
    navigate('/login', { replace: true })
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-950 text-slate-100">
      <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur">
        <div className="flex items-center h-12 px-4 gap-3">
          <Link to="/projects" className="flex items-center gap-2.5">
            <span className="w-6 h-6 rounded-md bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center shadow-lg">
              <Sparkles className="h-3.5 w-3.5 text-white" />
            </span>
            <span className="font-semibold tracking-tight">{APP_BRAND}</span>
            <span className="text-slate-500 text-xs">{APP_DISPLAY_VERSION}</span>
          </Link>
          <div className="h-5 w-px bg-white/10 mx-1" />
          <Link to="/projects" className="text-xs text-slate-400 hover:text-white">
            Projects
          </Link>
          <div className="ml-auto flex items-center gap-1">
            <button
              type="button"
              title="Keyboard shortcuts (press ?)"
              aria-label="Open keyboard shortcuts"
              onClick={() => onOpenShortcuts?.()}
              className="p-2 rounded-md text-slate-400 hover:bg-white/5 hover:text-slate-200"
            >
              <Keyboard className="h-4 w-4" />
            </button>
            <button
              type="button"
              data-testid="theme-cycle"
              data-theme={theme}
              title={`${THEME_LABEL[theme]} theme · click for ${NEXT_LABEL[theme]} (T)`}
              aria-label={`Theme: ${THEME_LABEL[theme]}. Click to switch to ${NEXT_LABEL[theme]}.`}
              onClick={cycleTheme}
              className="p-2 rounded-md text-slate-400 hover:bg-white/5 hover:text-slate-200 inline-flex items-center justify-center w-8 h-8"
            >
              {/* Icon mirrors the CURRENT theme (user-expected
                  semantics): sun = light, moon = dark, "C" = carbon. */}
              {theme === 'light' ? (
                <Sun className="h-4 w-4" aria-hidden />
              ) : theme === 'dark' ? (
                <Moon className="h-4 w-4" aria-hidden />
              ) : (
                <span
                  aria-hidden
                  className="font-bold text-[13px] leading-none tracking-tight"
                >
                  C
                </span>
              )}
            </button>
            {authEnabled && user && (
              <>
                <div className="h-5 w-px bg-white/10 mx-1" />
                <span
                  className="text-xs text-slate-400 px-1 hidden sm:inline"
                  data-testid="auth-user"
                >
                  {user}
                </span>
                <button
                  type="button"
                  title="Sign out"
                  aria-label="Sign out"
                  data-testid="logout"
                  onClick={handleLogout}
                  className="p-2 rounded-md text-slate-400 hover:bg-white/5 hover:text-slate-200 inline-flex items-center justify-center w-8 h-8"
                >
                  <LogOut className="h-4 w-4" aria-hidden />
                </button>
              </>
            )}
          </div>
        </div>
      </header>
      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  )
}
